/*
 * Reine Logik der Seite „Geräte-Freigaben" (/settings/device-registrations),
 * ohne DOM, damit sie mit node:test prüfbar ist.
 *
 * Der Code-Vergleich spiegelt RegistrationCrypto.NormaliseCode auf dem
 * Server (Crockford-Base32: O → 0, I/L → 1, Trenner egal). Die Oberfläche
 * prüft nur vor, entscheiden tut der Server.
 */

export type RegistrationStatus = 'pending' | 'approved' | 'rejected';

// GET /api/device-registrations
export interface DeviceRegistrationDto {
  id: string;
  code: string;
  chipSerial: string;
  nameplateSerial: string | null;
  hostname: string | null;
  chassis: string | null;
  clientVersion: string | null;
  status: RegistrationStatus;
  firstSeenAt: string;
  lastSeenAt: string;
  lastSourceIp: string | null;
  plantId: string | null;
  clientId: string | null;
  decidedAt: string | null;
  collectedAt: string | null;
}

export interface ListDeviceRegistrationsResponse {
  registrations: DeviceRegistrationDto[];
}

export const CODE_LENGTH = 8;

export function normaliseCode(typed: string | null | undefined): string {
  if (!typed) return '';
  let out = '';
  for (const raw of typed) {
    if (raw === ' ' || raw === '-' || raw === '_') continue;
    const c = raw.toUpperCase();
    out += c === 'O' ? '0' : c === 'I' || c === 'L' ? '1' : c;
  }
  return out;
}

/** Passt der getippte Code zur Anmeldung? */
export function codesMatch(typed: string, registrationCode: string): boolean {
  const a = normaliseCode(typed);
  return a.length === CODE_LENGTH && a === normaliseCode(registrationCode);
}

/** Vollständig getippt, aber falsch: dann zeigt die Oberfläche den Fehler
 *  schon vor dem Absenden. Unvollständig ist noch kein Fehler. */
export function codeMismatch(typed: string, registrationCode: string): boolean {
  return normaliseCode(typed).length === CODE_LENGTH && !codesMatch(typed, registrationCode);
}

export interface PlantOption {
  id: string;
  displayName: string;
  address: string | null;
}

/** Anlagen nach Name oder Adresse filtern, alle Wörter müssen vorkommen. */
export function filterPlants<T extends PlantOption>(plants: T[], query: string, limit = 50): T[] {
  const words = query.toLowerCase().split(/\s+/).filter((w) => w.length > 0);
  const hits = words.length === 0
    ? plants
    : plants.filter((p) => {
        const hay = `${p.displayName} ${p.address ?? ''}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      });
  return [...hits]
    .sort((a, b) => a.displayName.localeCompare(b.displayName, 'de'))
    .slice(0, limit);
}

export function statusLabel(r: DeviceRegistrationDto): string {
  switch (r.status) {
    case 'pending':
      return 'Wartet';
    case 'rejected':
      return 'Abgelehnt';
    case 'approved':
      return r.collectedAt ? 'Eingerichtet' : 'Freigegeben, noch nicht abgeholt';
  }
}

export function statusTone(r: DeviceRegistrationDto): 'warn' | 'ok' | 'error' | 'info' {
  switch (r.status) {
    case 'pending':
      return 'warn';
    case 'rejected':
      return 'error';
    case 'approved':
      return r.collectedAt ? 'ok' : 'info';
  }
}

/** „vor 3 min", „vor 2 h", „vor 4 Tagen"; ältere als Datum. */
export function formatRelative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso);
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000);
  if (Number.isNaN(seconds)) return '';
  if (seconds < 60) return 'gerade eben';
  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return `vor ${minutes} min`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `vor ${hours} h`;
  const days = Math.round(hours / 24);
  if (days < 14) return days === 1 ? 'vor 1 Tag' : `vor ${days} Tagen`;
  return then.toLocaleDateString('de-AT');
}
